import React, { useMemo, useState } from "react";
import {
  Overlay,
  ModalCard,
  CloseBtn,
  Content,
  GalleryCol,
  MainImage,
  ThumbsRow,
  Thumb,
  InfoCol,
  ProductTitle,
  ShortDescription,
  StatusRow,
  StockBadge,
  Divider,
  TabsRow,
  TabButton,
  TabPanel,
  SectionText,
  AddToCartButton,
  NutritionTable,
  NutritionRow,
  NutritionLabel,
  NutritionValue,
  NutritionNote,
  ProductGramajLine,
} from "./ProductDetailsModalStyles";

const TAB_DETALII = "detalii";
const TAB_INGREDIENTE = "ingrediente";
const TAB_ALERGENI = "alergeni";
const TAB_NUTRITIE = "nutritie";

function formatGramaj(gramaj) {
  const value = Number(gramaj);

  if (!Number.isFinite(value) || value <= 0) return "";

  const pretty = Number.isInteger(value)
    ? value.toString()
    : value.toFixed(2).replace(/\.?0+$/, "");

  return `Cantitate (+/- 10%): ${pretty} g`;
}

export default function ProductDetailsModal({ product, onClose, onAddToCart }) {
  const images = useMemo(() => {
    if (product?.imagini?.length) {
      return product.imagini.map((x) => x.image_url);
    }
    if (product?.imagine) return [product.imagine];
    return [];
  }, [product]);

  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState(TAB_DETALII);

  if (!product) return null;

  const esteInStoc =
    (product.disponibil ?? true) && Number(product.stoc ?? 0) > 0;

  const shortDescription = product.descriere?.trim() || "";
  const longDescription =
    product.descriere_lunga?.trim() || "Momentan nu sunt afișate detalii.";

  const ingrediente =
    product.ingrediente?.trim() || "Momentan nu sunt afișate ingrediente.";

  const alergeni =
    product.alergeni?.trim() || "Momentan nu sunt afișați alergeni.";

  const nutritionItems = [
    {
      label: "Calorii",
      value:
        product.calorii_kcal != null
          ? `${Number(product.calorii_kcal).toFixed(2)} kcal`
          : null,
    },
    {
      label: "Proteine",
      value:
        product.proteine_g != null
          ? `${Number(product.proteine_g).toFixed(2)} g`
          : null,
    },
    {
      label: "Carbohidrați",
      value:
        product.carbohidrati_g != null
          ? `${Number(product.carbohidrati_g).toFixed(2)} g`
          : null,
    },
    {
      label: "Grăsimi",
      value:
        product.grasimi_g != null
          ? `${Number(product.grasimi_g).toFixed(2)} g`
          : null,
    },
    {
      label: "Sare",
      value:
        product.sare_g != null
          ? `${Number(product.sare_g).toFixed(2)} g`
          : null,
    },
    {
      label: "Zaharuri",
      value:
        product.zaharuri_g != null
          ? `${Number(product.zaharuri_g).toFixed(2)} g`
          : null,
    },
  ];

  const hasNutrition = nutritionItems.some((item) => item.value);

  return (
    <Overlay onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <CloseBtn onClick={onClose} aria-label="Închide">
          ×
        </CloseBtn>

        <Content>
          <GalleryCol>
            {!!images.length && (
              <>
                <MainImage src={images[activeImage]} alt={product.nume} />

                {images.length > 1 && (
                  <ThumbsRow>
                    {images.map((src, index) => (
                      <Thumb
                        key={`${src}-${index}`}
                        src={src}
                        alt={`${product.nume} ${index + 1}`}
                        $active={index === activeImage}
                        onClick={() => setActiveImage(index)}
                      />
                    ))}
                  </ThumbsRow>
                )}
              </>
            )}
          </GalleryCol>

          <InfoCol>
            <ProductTitle>{product.nume}</ProductTitle>

            {!!shortDescription && (
              <ShortDescription>{shortDescription}</ShortDescription>
            )}

            {!product.gramaj_selectabil && !!product.gramaj && (
              <ProductGramajLine>
                {formatGramaj(product.gramaj)}
              </ProductGramajLine>
            )}

            <StatusRow>
              <StockBadge $inStock={esteInStoc}>
                {esteInStoc ? "Disponibil" : "Stoc epuizat"}
              </StockBadge>
            </StatusRow>

            <Divider />

            <TabsRow>
              <TabButton
                type="button"
                $active={activeTab === TAB_DETALII}
                onClick={() => setActiveTab(TAB_DETALII)}
              >
                Detalii
              </TabButton>

              <TabButton
                type="button"
                $active={activeTab === TAB_INGREDIENTE}
                onClick={() => setActiveTab(TAB_INGREDIENTE)}
              >
                Ingrediente
              </TabButton>

              <TabButton
                type="button"
                $active={activeTab === TAB_ALERGENI}
                onClick={() => setActiveTab(TAB_ALERGENI)}
              >
                Alergeni
              </TabButton>

              <TabButton
                type="button"
                $active={activeTab === TAB_NUTRITIE}
                onClick={() => setActiveTab(TAB_NUTRITIE)}
              >
                Nutriție
              </TabButton>
            </TabsRow>

            <TabPanel>
              {activeTab === TAB_DETALII && (
                <SectionText>{longDescription}</SectionText>
              )}

              {activeTab === TAB_INGREDIENTE && (
                <SectionText>{ingrediente}</SectionText>
              )}

              {activeTab === TAB_ALERGENI && (
                <SectionText>{alergeni}</SectionText>
              )}

              {activeTab === TAB_NUTRITIE && (
                <>
                  {hasNutrition ? (
                    <>
                      <NutritionTable>
                        {nutritionItems
                          .filter((item) => item.value)
                          .map((item) => (
                            <NutritionRow key={item.label}>
                              <NutritionLabel>{item.label}</NutritionLabel>
                              <NutritionValue>{item.value}</NutritionValue>
                            </NutritionRow>
                          ))}
                      </NutritionTable>

                      <NutritionNote>
                        Valorile nutriționale sunt afișate conform datelor
                        disponibile pentru acest produs.
                      </NutritionNote>
                    </>
                  ) : (
                    <SectionText>
                      Momentan nu sunt afișate informații nutriționale pentru
                      acest produs.
                    </SectionText>
                  )}
                </>
              )}
            </TabPanel>

            <AddToCartButton
              type="button"
              disabled={!esteInStoc}
              onClick={() => {
                onAddToCart?.(product);
                onClose?.();
              }}
            >
              {esteInStoc ? "Adaugă în coș" : "Stoc epuizat"}
            </AddToCartButton>
          </InfoCol>
        </Content>
      </ModalCard>
    </Overlay>
  );
}
